import React, { useState, useRef, useEffect } from 'react';
import { useSizeSelector } from './useSizeSelector';
import './SizeSelector.scss';
import { useI18n } from '../../../../contexts/I18nContext';
import { ChevronDown, Check, Monitor, Image as ImageIcon, Zap, Crown } from 'lucide-react';
import banana2Icon from '../../../../assets/banana2.webp';
import seedreamIcon from '../../../../assets/seedream.svg';

// 图标映射
const ICONS = {
  model: Zap,
  aspectRatio: ImageIcon,
  resolution: Monitor
};

const SizeSelector = ({
  title,
  required = false,
  value,
  onChange,
  disabled = false,
  props = {}
}) => {
  const { t } = useI18n();
  const {
    model,
    aspectRatio,
    resolution,
    modelOptions,
    aspectRatioOptions,
    resolutionOptions,
    handleModelChange,
    handleAspectRatioChange,
    handleResolutionChange
  } = useSizeSelector({ value, onChange });

  return (
    <div className="size-selector-container">
      <div className="component-header">
        <h3 className="component-title">
          {t('aiToolTemplate.generationSettings') || 'Generation Settings'}
        </h3>
      </div>
      
      <div className="size-selector-row">
        <CustomSelect
          label={t('aiToolTemplate.model')}
          value={model}
          options={modelOptions}
          onChange={handleModelChange}
          disabled={disabled}
          icon={ICONS.model}
          type="model"
        />
        <CustomSelect
          label={t('aiToolTemplate.aspectRatio')}
          value={aspectRatio}
          options={aspectRatioOptions}
          onChange={handleAspectRatioChange}
          disabled={disabled}
          icon={ICONS.aspectRatio}
          type="aspectRatio"
        />
        <CustomSelect
          label={t('aiToolTemplate.resolution')}
          value={resolution}
          options={resolutionOptions}
          onChange={handleResolutionChange}
          disabled={disabled}
          icon={ICONS.resolution}
          type="resolution"
        />
      </div>
    </div>
  );
};

const CustomSelect = ({ label, value, options, onChange, disabled, icon: Icon, type }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value);

  // 点击外部关闭下拉
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (val) => {
    if (disabled) return;
    onChange(val);
    setIsOpen(false);
  };

  // 根据类型解析标签和描述
  const parseOptionLabel = (opt) => {
    // 针对尺寸和分辨率，强制使用 value 作为主标题（上面显示），label 作为副标题（下面显示）
    if (type === 'aspectRatio' || type === 'resolution') {
        const title = opt.value;
        const desc = opt.label;

        // 如果 title 和 desc 完全一样，就不显示 desc 了，避免重复
        if (title === desc) {
            return { title, desc: '' };
        }
        
        // 针对分辨率特殊处理：去掉 label 里包含的 value 部分
        // 例如 value="2K", label="2K 2048x2048" -> desc="2048x2048"
        if (type === 'resolution' && desc.startsWith(title)) {
            const cleanDesc = desc.replace(title, '').trim();
            return { title, desc: cleanDesc };
        }
        
        // 针对比例特殊处理：去掉 label 里包含的 value 部分
        // 例如 value="16:9", label="16:9 宽屏" -> desc="宽屏"
        if (type === 'aspectRatio' && desc.startsWith(title)) {
             const cleanDesc = desc.replace(title, '').trim();
             return { title, desc: cleanDesc };
        }

        return { title, desc };
    }

    // 针对模型 (model)
    // 效果图: 上面是 label (如 "Gemini"), 下面是 value (如 "gemini-pro")
    if (type === 'model') {
        const title = opt.label || opt.value;
        const desc = opt.value;
        
        // 如果 title 和 desc 一样，就只显示 title
        if (title.toLowerCase() === desc.toLowerCase()) {
             return { title, desc: '' };
        }
        
        return { title, desc };
    }

    // 其他情况保持原样
    const fullLabel = opt.label || opt.value;
    
    // 1. 如果有 description 字段，优先使用
    if (opt.description) {
        return {
            title: fullLabel,
            desc: opt.description
        };
    }
    
    // 4. 默认简单拆分
    const parts = fullLabel.split(' ');
    if (parts.length > 1) {
      return {
        title: parts[0],
        desc: parts.slice(1).join(' ')
      };
    }
    
    return { title: fullLabel, desc: '' };
  };

  // 渲染 Trigger 区域的内容
  const renderTriggerContent = () => {
    if (!selectedOption) return <span className="placeholder">Select...</span>;
    
    const { title, desc } = parseOptionLabel(selectedOption);
    
    return (
      <div className="trigger-content">
        <span className="trigger-title">{title}</span>
        {desc && <span className="trigger-desc">{desc}</span>}
      </div>
    );
  };
  
  // 渲染指示器 (颜色块或进度条)
  const renderIndicator = (isActive, optionOverride) => {
      // 如果有 optionOverride (如下拉列表中)，则使用该选项的值；否则使用当前选中的 selectedOption
      const targetOption = optionOverride || selectedOption;
      
      if (type === 'model') {
          // 模型图标逻辑
          const val = targetOption ? (targetOption.value || '').toLowerCase() : '';
          const label = targetOption ? (targetOption.label || '').toLowerCase() : '';
          
          if (val.includes('gemini') || val.includes('nano')) {
              // Gemini 图标 (使用本地 banana2.webp)
              return (
                 <div className="model-icon-wrapper">
                    <img src={banana2Icon} alt="gemini" className="gemini-icon" />
                 </div>
              );
          } else if (val.includes('seedream')) {
              // Seedream 图标 (使用本地 seedream.svg)
              return (
                  <div className="model-icon-wrapper">
                      <img src={seedreamIcon} alt="seedream" className="seedream-icon" />
                  </div>
              );
          } else if (val.includes('openai') || label.includes('gpt')) {
              return (
                  <div className="model-icon-wrapper">
                      <span className="generic-model-icon gpt-icon">GPT</span>
                  </div>
              );
          }
          
          // 默认 fallback
          return <div className={`color-indicator indicator-block ${isActive ? 'active' : ''}`} />;
      }

      if (type === 'aspectRatio') {
          // 比例：绿色圆角矩形
          // 根据设计图：16:9 是横长条，9:16 是竖长条，1:1 是正方形，Auto 是默认块
          let styleClass = 'indicator-block';
          
          if (targetOption) {
              const val = targetOption.value; // e.g. "16:9"
              if (val.includes(':')) {
                  const [w, h] = val.split(':').map(Number);
                  if (w > h) styleClass += ' landscape';
                  else if (h > w) styleClass += ' portrait';
                  else styleClass += ' square';
              }
          }
          
          return <div className={`color-indicator ${styleClass} ${isActive ? 'active' : ''}`} />;
      } 
      
      if (type === 'resolution') {
          // 分辨率：蓝色进度条
          // 根据 1K/2K/4K 决定宽度
          let widthClass = 'w-medium'; // default 2K
          if (targetOption) {
              const val = (targetOption.value || '').toUpperCase();
              const label = (targetOption.label || '').toUpperCase();
              
              if (val.includes('1K') || label.includes('1K')) widthClass = 'w-short';
              else if (val.includes('4K') || label.includes('4K')) widthClass = 'w-long';
          }
          
          return (
            <div className="resolution-indicator-bg">
               <div className={`resolution-indicator-fill ${widthClass}`} />
            </div>
          );
      }
      
      return null;
  };

  return (
    <div className="custom-select-container" ref={containerRef}>
      {label && (
        <div className="select-label-row">
          {Icon && <Icon size={14} className="label-icon" />}
          <span className="select-label">{label}</span>
        </div>
      )}
      
      <div 
        className={`select-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="trigger-left">
           {/* 指示器 */}
           {renderIndicator(true)}
           
           {/* 文本内容 */}
           {renderTriggerContent()}
        </div>
        <ChevronDown size={16} className={`chevron ${isOpen ? 'rotated' : ''}`} />
      </div>

      {isOpen && !disabled && (
        <div className="select-dropdown">
          {options.length === 0 ? (
            <div className="no-options">No options</div>
          ) : (
            options.map(opt => {
              const { title, desc } = parseOptionLabel(opt);
              const isSelected = opt.value === value;
              const isPro = (opt.label && opt.label.includes('PRO')) || opt.isPro; // 简单判断
              
              // 针对选项重新计算指示器样式 (因为要根据每个 opt 的值来变)
              const renderOptionIndicator = () => {
                  return renderIndicator(isSelected, opt);
              };

              return (
                <div 
                  key={opt.value} 
                  className={`select-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleSelect(opt.value)}
                >
                  <div className="option-left">
                    <div className={`indicator-wrapper ${type === 'resolution' ? 'resolution-wrapper' : ''}`}>
                        {renderOptionIndicator()}
                    </div>
                    <div className="option-text">
                      <span className="option-title">{title}</span>
                      {desc && <span className="option-desc">{desc}</span>}
                    </div>
                  </div>
                  <div className="option-right">
                     {isPro && (
                         <span className="pro-badge">
                             <Crown size={10} style={{ marginRight: 2 }} /> PRO+
                         </span>
                     )}
                     {isSelected && <Check size={16} className="check-icon" />}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default SizeSelector;
